import Modal, { ModalFooter, Prompt } from '@ingka/modal';
import React, { useEffect, useMemo, useState } from 'react';
import SkapaTable, { TableBody as SkapaTableBody, TableHeader as SkapaTableHeader } from '@ingka/table';
import { StyledTableDataDiv, StyledTableRow } from '../../../../shared/styles/components/table.styles';
import { StyledToolbarButton, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMutation, useQueryClient } from 'react-query';
import { useSelectionState, useSubItemsFromSelections } from '../../state/selection.state';

import APIService from '../../../../services/api.service';
import { AxiosError } from 'axios';
import Button from '@ingka/button';
import { ToastService } from '../../../../services/toast.service';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import trashIcon from '@ingka/ssr-icon/paths/trash-can';
import { useTableDataState } from '../../state/table-data.state';
import { trackComponentAccess } from '../../../../utils';
import { AppComponent } from '../../../../constants';

export const DeletePrompt = () => {
   const selectedSubItems = useSubItemsFromSelections();
   const [filteredSubItems, setFilteredSubItems] = useState(selectedSubItems.filter((s) => s.clientCreated));
   const [isOpen, setIsOpen] = useState(false);

   useEffect(() => {
      setFilteredSubItems(selectedSubItems.filter((s) => s.clientCreated));
   }, [selectedSubItems]);

   const columns = useMemo(
      () => [
         {
            accessorKey: 'parentId',
            header: 'Parent Item',
         },
         {
            header: 'part',
            accessorKey: 'part',
         },
         {
            header: 'Material grade',
            accessorKey: 'materialGrade',
         },
      ],
      []
   );

   const instance = useReactTable({
      data: filteredSubItems,
      columns,
      state: {},
      getCoreRowModel: getCoreRowModel(),
   });

   const queryClient = useQueryClient();
   const query = useTableDataState((state) => state.query);
   const resetSelection = useSelectionState((state) => state.resetSelection);

   const { mutate } = useMutation<void, AxiosError<any>, typeof filteredSubItems>({
      mutationFn: (items) => APIService.deleteSubItems(items),
      onSuccess: () => {
         queryClient.invalidateQueries(['data', query]).then(() => {
            const materialGradeIds = filteredSubItems.map((item) => item.materialGradeId).join(',');
            resetSelection(query.pageSize);
            trackComponentAccess(AppComponent.DELETE_PROMPT, {
               deletedSubItemsCount: filteredSubItems.length,
               materialGradeIds,
                  });
            ToastService.success('Material(s) successfully deleted');
         });
      },
      onError: (error: AxiosError) => {
         ToastService.error(error.message);
      },
   });

   const onConfirm = () => {
      mutate(filteredSubItems);
      setIsOpen(false);
   };

   const tooltipText = useMemo(
      () => (filteredSubItems.length === 0 ? 'You need to select user created row(s) to delete' : 'Delete'),
      [filteredSubItems]
   );

   return (
      <>
         <StyledTooltip title={tooltipText}>
            <span>
               <StyledToolbarButton
                  ssrIcon={trashIcon}
                  text="Filter"
                  iconOnly={true}
                  small={true}
                  disabled={filteredSubItems.length === 0}
                  onClick={() => setIsOpen(true)}
               />
            </span>
         </StyledTooltip>
         <Modal visible={isOpen} escapable={false} handleCloseBtn={() => setIsOpen(false)}>
            <StyledPrompt
               title="Are you sure you want to delete?"
               footer={
                  <ModalFooter compact={true}>
                     <div style={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
                        <div
                           style={{
                              display: 'flex',
                              justifyContent: 'end',
                              justifySelf: 'end',
                              width: '50%',
                              paddingRight: tokens.space.md,
                           }}
                        >
                           <Button text="Keep" type="secondary" onClick={() => setIsOpen(false)} small={true} />
                           <Button text="Delete" type="primary" onClick={onConfirm} small={true} />
                        </div>
                     </div>
                  </ModalFooter>
               }
            >
               <StyledDeleteTable>
                  <SkapaTableHeader>
                     {instance.getHeaderGroups().map((headerGroup) => (
                        <StyledTableRow key={headerGroup.id} noHover={true}>
                           {headerGroup.headers.map((header,) => {
                              return (
                                 <th key={header.id} colSpan={header.colSpan}>
                                    <StyledTableDataDiv>
                                       {flexRender(header.column.columnDef.header, header.getContext())}
                                    </StyledTableDataDiv>
                                 </th>
                              );
                           })}
                        </StyledTableRow>
                     ))}
                  </SkapaTableHeader>

                  <SkapaTableBody>
                     {instance.getRowModel().rows.map((row) => {
                        return (
                           <StyledTableRow key={row.id}>
                              {row.getVisibleCells().map((cell) => {
                                 return (
                                    <td key={cell.id}>
                                       <StyledTooltip title={cell.getValue() + ''}>
                                          <StyledDeleteTableDataDiv>
                                             {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                          </StyledDeleteTableDataDiv>
                                       </StyledTooltip>
                                    </td>
                                 );
                              })}
                           </StyledTableRow>
                        );
                     })}
                  </SkapaTableBody>
               </StyledDeleteTable>
            </StyledPrompt>
         </Modal>
      </>
   );
};

const StyledPrompt = styled(Prompt)`
   border-radius: 0 !important;

   .prompt__content-wrapper {
      min-width: 40rem;
      padding: 1rem;
   }

   h2 {
      padding-top: ${tokens.space.lg};
      position: sticky;
   }
`;

const StyledDeleteTable = styled(SkapaTable)`
  table {
    white-space: nowrap !important;
    table-layout: fixed !important;
    text-overflow: ellipsis;
    width: 100%;
    border: ${tokens.border.thin} ${theme.colors.border.default} !important;
    border-radius: 0;
    border-collapse: separate;
    padding: ${tokens.space.xxs};

    td {
      padding: 1rem !important;
      font-size: ${tokens.font.size.md};
    }

    tr:last-child {
      td {
        border-bottom: none;
      }
    }

    th:first-of-type {
      width: 7rem;
    }

    th:nth-of-type(2) {
      width: 10rem;
    }

    th {
      padding: 1rem !important;
    }
`;

const StyledDeleteTableDataDiv = styled.div`
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;
